<template>
    <CustomModal
        content-class="bg-[var(--ui-bg)] shadow-md p-6 flex flex-col border border-[var(--ui-border)] rounded w-96"
        overlay-class="bg-black/75 backdrop-blur-sm"
        :in-progress="inProgress"
        :open="isOpen"
        :hide-close="isForcedAuth"
        @close="!isForcedAuth && close()"
    >
        <template #body>
            <AuthLogin
                v-if="authFlowView === 'login'"
                :key="String(authFlowView)"
                @login-failed="loginFailed"
                @login-success="loginSuccess"
                @open-sign-up="setAuthView"
                @open-forgot-password="setAuthView"
            />
            <AuthSignUp
                v-if="authFlowView === 'signup'"
                :key="String(authFlowView)"
                @sign-up-failed="signUpFailed"
                @sign-up-success="signUpSuccess"
                @open-login="setAuthView"
            />
            <AuthForgetPassword
                v-if="authFlowView === 'forgot-password'"
                :key="String(authFlowView)"
                @send-otp-success="handlesendOtpSuccess"
                @send-otp-failed="handlesendOtpFailed"
                @open-login="setAuthView"
            />
            <AuthResetPasswordWithOtp
                v-if="authFlowView === 'reset-password-with-otp'"
                :key="String(authFlowView)"
                :email="forgotPasswordEmail"
                @reset-password-with-otp-success="handleResetPasswordSuccess"
                @reset-password-with-otp-failed="handleResetPasswordFailed"
                @open-login="setAuthView"
            />
        </template>
    </CustomModal>
</template>

<script setup lang="ts">
// imports
// import { storeToRefs } from 'pinia'
// TODO(better-auth): this entire auth flow is currently Magento/ACCS-specific and will need
// to be reconciled with the Better Auth-based app user model in a future phase.
import { showToastNotification } from '~~/layers/base/utils/commonUtils'
import { AuthView } from '~~/layers/base/types/auth'
import type { ForgotPassword } from '~~/layers/base/types/auth'
import { getErrorMessage } from '~~/layers/base/services/util.service'
import { splitAuthErrorMessage } from '~~/layers/base/data/auth'

const { isOpen, authFlowView, close, setAuthView, isForcedAuth } = useAuthFlow()

// pinia store instances
const customerAuthStore = useCustomerAuthAccsStore()

// reactive value from store
const { inProgress } = storeToRefs(customerAuthStore)

// Email entered on the "forgot password" step, threaded through to the OTP reset step
// (Better Auth's reset-password call needs it; it isn't part of the OTP form itself).
const forgotPasswordEmail = ref('')

function showAuthErrorToast(errorObject: unknown): void {
    const message = getErrorMessage(errorObject)
    const { title, body } = splitAuthErrorMessage(message)
    showToastNotification({
        title: body ? title : 'Error',
        description: body ?? title,
        color: 'error',
    })
}

const loginSuccess = (): void => {
    showToastNotification({
        title: 'Success',
        description: 'Login in successfully',
        color: 'primary',
    })
    close()
}
const loginFailed = (errorObject: unknown): void => {
    showAuthErrorToast(errorObject)
}

const signUpSuccess = (): void => {
    showToastNotification({
        title: 'Success',
        description: 'User registered successfully',
        color: 'primary',
    })
    close()
}

const signUpFailed = (errorObject: unknown): void => {
    showAuthErrorToast(errorObject)
}
// forget password
const handlesendOtpSuccess = (data: ForgotPassword): void => {
    forgotPasswordEmail.value = data.email
    setAuthView(AuthView.ResetPasswordWithOtp)
    showToastNotification({
        title: 'Success',
        description: `If there is an account associated with the provided email, you will receive an otp to reset your password.`,
        color: 'primary',
    })
}

const handlesendOtpFailed = (errorObject: unknown): void => {
    showAuthErrorToast(errorObject)
}
const handleResetPasswordSuccess = (): void => {
    setAuthView(AuthView.Login)
    showToastNotification({
        title: 'Success',
        description: 'Password reset successfully, you can now login in with your new password.',
        color: 'primary',
    })
}
const handleResetPasswordFailed = (errorObject: unknown): void => {
    showAuthErrorToast(errorObject)
}
</script>
